import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, Pencil, MapPin, Video, AlignLeft, Calendar as CalendarIcon, Trash2, Car } from 'lucide-react';
import { getCalendars } from '../api';
import { Badge } from './catalyst/badge';
import { Button } from './catalyst/button';
import { Dialog, DialogBody, DialogTitle } from './catalyst/dialog';
import { Divider } from './catalyst/divider';
import { Text } from './catalyst/text';

const EVENT_COLORS = {
  '1': { bg: '#2563EB' },
  '2': { bg: '#E11D48' },
  '3': { bg: '#059669' },
  '4': { bg: '#71717A' }
};

const EVENT_COLORS_SOFT = {
  '1': { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  '2': { bg: '#FFF1F2', text: '#BE123C', border: '#FECDD3' },
  '3': { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' },
  '4': { bg: '#F4F4F5', text: '#3F3F46', border: '#D4D4D8' }
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// Mini timeline showing ~4 hours around the event
function MiniTimeline({ event, allEvents }) {
  const isAllDay = !event.start?.dateTime;
  if (isAllDay) return null;

  const eventStart = new Date(event.start.dateTime);
  const eventEnd = new Date(event.end.dateTime);
  const eventDay = new Date(eventStart);
  eventDay.setHours(0, 0, 0, 0);

  // Show a window: 1 hour before event start to 1 hour after event end, minimum 4 hours
  const eventDurationHours = (eventEnd - eventStart) / (1000 * 60 * 60);
  const windowHours = Math.max(4, eventDurationHours + 2);
  const windowStartHour = Math.max(0, Math.floor(eventStart.getHours() - 1));
  const windowEndHour = Math.min(24, windowStartHour + windowHours);

  // Filter events that overlap with this time window on the same day
  const dayEvents = allEvents.filter(e => {
    if (!e.start?.dateTime) return false;
    const s = new Date(e.start.dateTime);
    const eDay = new Date(s);
    eDay.setHours(0, 0, 0, 0);
    if (eDay.getTime() !== eventDay.getTime()) return false;
    const eEnd = new Date(e.end.dateTime);
    const windowStart = new Date(eventDay);
    windowStart.setHours(windowStartHour, 0, 0, 0);
    const windowEnd = new Date(eventDay);
    windowEnd.setHours(windowEndHour, 0, 0, 0);
    return s < windowEnd && eEnd > windowStart;
  });

  const hourHeight = 48;
  const totalHeight = (windowEndHour - windowStartHour) * hourHeight;

  const getPosition = (dateTime) => {
    const d = new Date(dateTime);
    const hours = d.getHours() + d.getMinutes() / 60;
    return (hours - windowStartHour) * hourHeight;
  };

  const hours = [];
  for (let h = windowStartHour; h < windowEndHour; h++) {
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    hours.push({ hour: h, label: `${displayHour}:00` });
  }

  return (
    <div className="mx-0 my-4 overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50">
      <div style={{ position: 'relative', height: totalHeight, marginLeft: '52px', marginRight: '12px' }}>
        {/* Hour lines and labels */}
        {hours.map(({ hour, label }) => (
          <div key={hour} style={{ position: 'absolute', top: (hour - windowStartHour) * hourHeight, width: '100%' }}>
            <span style={{
              position: 'absolute',
              left: '-48px',
              top: '-8px',
              fontSize: '11px',
              color: '#71717A',
              width: '40px',
              textAlign: 'right'
            }}>
              {label}
            </span>
            <div style={{ borderTop: '1px solid #E4E4E7', width: '100%' }} />
          </div>
        ))}

        {/* Events */}
        {dayEvents.map(e => {
          const top = Math.max(0, getPosition(e.start.dateTime));
          const bottom = Math.min(totalHeight, getPosition(e.end.dateTime));
          const height = Math.max(20, bottom - top);
          const isSelected = e.id === event.id;
          const colorId = e.colorId || '1';
          const colors = EVENT_COLORS[colorId] || EVENT_COLORS['1'];
          const softColors = EVENT_COLORS_SOFT[colorId] || EVENT_COLORS_SOFT['1'];

          const travelBefore = isSelected ? parseInt(e.extendedProperties?.shared?.travelBefore || '0', 10) : 0;
          const travelAfter = isSelected ? parseInt(e.extendedProperties?.shared?.travelAfter || '0', 10) : 0;
          const travelBeforeHeight = travelBefore * (hourHeight / 60);
          const travelAfterHeight = travelAfter * (hourHeight / 60);

          return (
            <React.Fragment key={e.id}>
              {travelBefore > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: Math.max(0, top - travelBeforeHeight),
                    left: '2px',
                    right: '2px',
                    height: Math.min(travelBeforeHeight, top),
                    borderRadius: '6px 6px 0 0',
                    backgroundColor: colors.bg,
                    opacity: 0.25,
                    borderBottom: `1.5px dashed ${colors.bg}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {travelBeforeHeight >= 16 && (
                    <span style={{ fontSize: '9px', color: colors.bg, fontWeight: 600 }}>
                      {travelBefore >= 60 ? `${Math.floor(travelBefore / 60)}h${travelBefore % 60 ? ` ${travelBefore % 60}m` : ''}` : `${travelBefore}m`}
                    </span>
                  )}
                </div>
              )}
              <div
                style={{
                  position: 'absolute',
                  top,
                  left: '2px',
                  right: '2px',
                  height,
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  fontWeight: 500,
                  overflow: 'hidden',
                  backgroundColor: isSelected ? colors.bg : softColors.bg,
                  color: isSelected ? '#fff' : softColors.text,
                  borderLeft: isSelected ? 'none' : `3px solid ${softColors.border}`,
                  opacity: isSelected ? 1 : 0.8
                }}
              >
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.summary}
                </div>
              </div>
              {travelAfter > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: top + height,
                    left: '2px',
                    right: '2px',
                    height: travelAfterHeight,
                    borderRadius: '0 0 6px 6px',
                    backgroundColor: colors.bg,
                    opacity: 0.25,
                    borderTop: `1.5px dashed ${colors.bg}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {travelAfterHeight >= 16 && (
                    <span style={{ fontSize: '9px', color: colors.bg, fontWeight: 600 }}>
                      {travelAfter >= 60 ? `${Math.floor(travelAfter / 60)}h${travelAfter % 60 ? ` ${travelAfter % 60}m` : ''}` : `${travelAfter}m`}
                    </span>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function EventViewModal({ event, allEvents = [], onClose, onEdit, onDelete }) {
  const [calendars, setCalendars] = useState([]);
  const [deleteState, setDeleteState] = useState('idle'); // 'idle' | 'confirm' | 'deleting'
  const confirmTimerRef = useRef(null);

  useEffect(() => {
    getCalendars().then(setCalendars).catch(() => {});
  }, []);

  // Prevent background scroll on mobile
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Reset confirm timer on unmount
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (deleteState === 'idle') {
      setDeleteState('confirm');
      confirmTimerRef.current = setTimeout(() => setDeleteState('idle'), 3000);
    } else if (deleteState === 'confirm') {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      // Close immediately and let parent handle optimistic delete
      onClose();
      if (onDelete) onDelete(event);
    }
  }, [deleteState, event, onClose, onDelete]);

  if (!event) return null;

  const isAllDay = !event.start?.dateTime;
  const startStr = event.start?.dateTime || event.start?.date;

  const dateDisplay = formatDate(startStr);
  const timeDisplay = isAllDay
    ? 'All day'
    : `${formatTime(event.start.dateTime)} \u2013 ${formatTime(event.end.dateTime)}`;

  const calendar = calendars.find(c => c.id === event.calendarId);
  const calendarName = calendar?.summary || event.calendarName || 'Calendar';
  const calendarColor = calendar?.backgroundColor || EVENT_COLORS[event.colorId || '1']?.bg || '#2563EB';

  const videoLink = event.hangoutLink || event.conferenceData?.entryPoints?.[0]?.uri;
  const videoLabel = videoLink?.includes('meet.google.com') ? 'Join Google Meet'
    : videoLink?.includes('zoom.us') ? 'Join Zoom Meeting'
    : videoLink ? 'Join Video Call' : null;

  return (
    <Dialog size="lg" onClose={onClose} className="h-[calc(100dvh-1rem)] overflow-hidden p-0 sm:h-auto sm:max-h-[90vh]">
      <div className="flex h-full flex-col sm:max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
          <Button plain onClick={onClose} className="px-2">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <Button outline onClick={() => onEdit(event)}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        </div>

        <DialogBody className="mt-0 flex-1 overflow-y-auto px-4 pb-6 pt-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="space-y-2">
            <DialogTitle className="text-2xl leading-tight">
              {event.summary || '(No title)'}
            </DialogTitle>
            <div className="flex flex-wrap gap-2">
              <Badge color="zinc">{dateDisplay}</Badge>
              <Badge color="blue">{timeDisplay}</Badge>
            </div>
          </div>

          <MiniTimeline event={event} allEvents={allEvents} />

          <div className="mt-4 overflow-hidden rounded-xl border border-zinc-200 bg-white">
            <div className="flex items-center gap-3 px-4 py-3">
              <CalendarIcon className="h-5 w-5 text-zinc-400" />
              <div className="flex min-w-0 items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: calendarColor }} />
                <span className="truncate text-sm font-medium text-zinc-950">{calendarName}</span>
              </div>
            </div>

            {(parseInt(event.extendedProperties?.shared?.travelBefore || '0') > 0 ||
              parseInt(event.extendedProperties?.shared?.travelAfter || '0') > 0) && (
              <>
                <Divider soft />
                <div className="flex items-center gap-3 px-4 py-3">
                  <Car className="h-5 w-5 text-zinc-400" />
                  <Text className="text-zinc-700">
                    {(() => {
                      const before = parseInt(event.extendedProperties.shared.travelBefore || '0');
                      const after = parseInt(event.extendedProperties.shared.travelAfter || '0');
                      const fmt = (m) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m} min`;
                      const parts = [];
                      if (before > 0) parts.push(`${fmt(before)} before`);
                      if (after > 0) parts.push(`${fmt(after)} after`);
                      return parts.join(' · ');
                    })()}
                  </Text>
                </div>
              </>
            )}

            {event.location && (
              <>
                <Divider soft />
                <div className="flex items-center gap-3 px-4 py-3">
                  <MapPin className="h-5 w-5 text-zinc-400" />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-700">{event.location}</span>
                </div>
              </>
            )}

            {videoLink && (
              <>
                <Divider soft />
                <div className="flex items-center gap-3 px-4 py-3">
                  <Video className="h-5 w-5 text-zinc-400" />
                  <a
                    href={videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-zinc-950 underline decoration-zinc-400 underline-offset-4 hover:decoration-zinc-950"
                  >
                    {videoLabel}
                  </a>
                </div>
              </>
            )}

            {event.description && (
              <>
                <Divider soft />
                <div className="flex items-start gap-3 px-4 py-3">
                  <AlignLeft className="mt-0.5 h-5 w-5 text-zinc-400" />
                  <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-zinc-700">{event.description}</p>
                </div>
              </>
            )}
          </div>

          {event.id && (
            <div className="pt-4">
              <Button
                onClick={handleDeleteClick}
                disabled={deleteState === 'deleting'}
                color={deleteState === 'confirm' || deleteState === 'deleting' ? 'red' : 'white'}
                outline={deleteState === 'idle'}
                className={`w-full ${deleteState === 'idle' ? 'text-red-600 hover:bg-red-50' : ''}`}
              >
                {deleteState === 'deleting' ? (
                  'Deleting...'
                ) : deleteState === 'confirm' ? (
                  'Click again to confirm'
                ) : (
                  <><Trash2 className="h-4 w-4" /> Delete event</>
                )}
              </Button>
            </div>
          )}
        </DialogBody>
      </div>
    </Dialog>
  );
}
